import { MutationTuple, ApolloError } from '@apollo/client';

import { HookResult } from '@/store/apolloClient';
import {
  LearningProgressFragmentDoc,
  LearningProgressCacheWriteFragmentDoc,
  RLearningProgressFragment,
  useCompleteLearningMutationRemote,
  useEffortForRealmQueryRemote,
} from '@/store/v2';

import { useCurrentUser } from '@/store/currentUser';
import { EffortForRealm } from '@/types/analytics';

export { GetUserEffortDocument as getEffortForUser } from '@/store/v2';

interface SetLearningCompletedResponse {
  setLearningCompleted: MutationTuple<any, { learningId: number }>[0];
  loading: boolean;
  error?: ApolloError;
}

export const useSetLearningCompleted = (learningId: number, onCompleted?: () => void): SetLearningCompletedResponse => {
  const [setLearningCompleted, { loading, error }] = useCompleteLearningMutationRemote({
    variables: { learningId },
    onCompleted,
    update(cache, { data, errors }) {
      if (!data || Boolean(errors)) return;
      const cacheId = cache.identify({ __typename: 'Learning', id: learningId });

      const fromCache = cache.readFragment<RLearningProgressFragment>({
        id: cacheId,
        fragment: LearningProgressFragmentDoc,
      });

      cache.writeFragment({
        id: cacheId,
        fragment: LearningProgressCacheWriteFragmentDoc,
        data: {
          progress: {
            __typename: fromCache?.progress?.__typename,
            completed: true,
            completedPercentage: 100,
            historical: false,
            expiredAt: fromCache?.progress?.expiredAt || null,
          },
        },
      });
    },
  });
  return {
    setLearningCompleted,
    loading,
    error,
  };
};

interface EffortPerRealmResponse extends HookResult {
  effortPerRealm: EffortForRealm[];
}

export const useEffortPerRealm = ({ from, to }: { from?: string; to?: string }): EffortPerRealmResponse => {
  const { user } = useCurrentUser();

  const { loading, refetch, networkStatus, error, data } = useEffortForRealmQueryRemote({
    variables: {
      fromTime: from,
      toTime: to,
      realm: user?.realm || '',
    },
    skip: !user?.realm,
  });

  const effortPerRealm: EffortForRealm[] =
    data?.reportingAggregationEffortForRealm?.map((data) => ({
      average_effort_per_user: data.averageEffort || 0,
      total_effort_for_realm: data.totalEffort || 0,
      p90: data.p90Effort || 0,
      p80: data.p80Effort || 0,
      p70: data.p70Effort || 0,
      p60: data.p60Effort || 0,
      p50: data.p50Effort || 0,
    })) || [];

  return {
    effortPerRealm,
    error,
    refetch,
    networkStatus,
    loading,
  };
};
